/* The part picker inside the fit check and backorder modals.

   WHAT THE FORM COULD NOT ASK. The fit check exists to answer "will these
   parts fit my trailer", and it captured neither the parts nor the trailer.
   The store already knows both by the time the modal opens: the part is the
   page we are standing on, and the VIN is in localStorage from the hero
   search. This fills them in and lets the customer add, swap and remove parts
   from there.

   IT OWNS THE SELECTION, NOT THE WRITING. Everything that touches a HubSpot
   field lives in assets/fx-fitwrite.js. This file publishes the selection on
   window.fxFitSelection and fires fx:fitparts when it changes; that one
   listens. Keeping them apart means the UI cannot be broken by a field rename
   and the writer cannot be broken by a layout change, and neither file has to
   grow past the size a theme asset write handles comfortably.

   NO <form>, NO name="q", NO type="search". The ExpertRec app on this store
   claims anything that looks like a storefront search box, stamps
   data-er-attached on it and takes our listeners with it. assets/fx-search.js
   learned this the hard way; the input below is name="fxq" and type="text" for
   the same reason. If the suggestions ever go dead, check for data-er-attached
   before looking anywhere else.

   THE DEBOUNCE IS setTimeout AND NOT requestAnimationFrame. rAF does not run
   in a tab that is not being painted: the queued flag never clears and every
   mutation after that is dropped. Same rule as assets/fx-ext-modal.js.

   TAPPING A PART OPENS ITS DETAILS, and that sheet lives in
   assets/fx-fitdetail.js. It reaches back in through window.fxFitApi to add
   and remove, so the selection still has exactly one owner. Everything this
   file draws stays inside the dialog; the sheet and its zoom are the things
   that render on document.body, because the extension's dialog clips and
   scrolls its own content and an overlay inside it scrolls away with the
   form.

   ONE DIALOG, TWO JOBS. The extension reuses .vcp-form for the fit check and
   for the backorder request, rewriting its title on open. Everything that
   differs between them is in COPY and in mode() below, read fresh every pass,
   because a value captured at build time is whichever flow opened the dialog
   first. */
(function () {
  'use strict';

  var DEBOUNCE = 180;
  var LIMIT = 8;
  var SUGGEST = 6;
  var queued = null;
  var timer = null;
  var seq = 0;

  var sel = [];
  var view = 'list';
  window.fxFitSelection = sel;

  /* The wording that differs between the two flows. Kept as data rather than
     branches scattered through the rendering, so adding a third form later is
     one entry here and nothing else. */
  var COPY = {
    fit: {
      label: 'Which parts are you asking about?',
      placeholder: 'Add another part by name or part number',
      empty: 'No parts chosen yet. Search below.'
    },
    backorder: {
      label: 'Which part do you need?',
      placeholder: 'Add another part that’s out of stock',
      empty: 'No part chosen yet. Search below.'
    },
    /* The support page form is for a part we could not show them, so the
       picker is a helper rather than the point. No empty state message: an
       optional field that nags about being empty is just noise. */
    request: {
      label: 'Is it close to a part you have seen? Optional',
      placeholder: 'Add a part by name or number',
      empty: ''
    }
  };

  function announce() {
    try {
      document.dispatchEvent(new CustomEvent('fx:fitparts'));
    } catch (e) { /* very old browser, the writer still polls */ }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Shopify's CDN resizes on demand, so a 48px thumbnail does not have to pull
     a 2000px product shot. The suggest payload hands us the full size URL. */
  function sized(url, w) {
    if (!url) return '';
    /* Liquid hands us a URL that already carries a width, the suggest payload
       hands us one that does not. Swap it where it exists so a 48px thumbnail
       never pulls the 1400px copy, and append it where it does not. */
    var swapped = url.replace(/([?&])width=[0-9]+/, '$1width=' + w);
    if (swapped !== url) return swapped;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function label(p) {
    return p.sku ? p.sku + ' - ' + p.title : p.title;
  }


  /* ------------------------------------------------------------- hosts -- */

  /* THREE FORMS, TWO SHELLS, ONE PICKER. On a part page the fit check and the
     backorder request share the extension's .vcp-form dialog. On /pages/support
     the parts request form is an ordinary embed that sections/fx-support.liquid
     mounts into #fxSupportForm, with no dialog, no extension title and a
     heading the page has already drawn.

     Everything that differs between the two shells is answered here, so the
     rest of the file never asks which page it is on. Read fresh every pass:
     the dialog is created and destroyed as it opens and closes. */
  function host() {
    var dialog = document.querySelector('.vcp-form__dialog');
    if (dialog) {
      var mount = dialog.querySelector('.vcp-form__mount');
      var sub = dialog.querySelector('.vcp-form__sub');
      if (mount && sub) {
        return { shell: 'modal', mount: mount, after: sub, before: null, heading: true };
      }
    }
    var sup = document.getElementById('fxSupportForm');
    if (sup && sup.parentNode) {
      return { shell: 'support', mount: sup, after: null, before: sup, heading: false };
    }
    return null;
  }

  /* WHICH FORM IS OPEN, read from the dialog title every time it is asked
     rather than cached. Several spellings map to backorder because the button,
     the HubSpot form and the extension's dialog title were renamed from
     "Notify me" on different days and will not all agree for a while. The fit
     check is the default, so a title nobody recognises behaves like the
     flow that can cope with anything. */
  function mode() {
    var h = host();
    if (h && h.shell === 'support') return 'request';
    var t = document.querySelector('.vcp-form__title');
    var s = (t ? t.textContent : '').toLowerCase();
    if (s.indexOf('fits') > -1 || s.indexOf('compat') > -1) return 'fit';
    if (s.indexOf('backorder') > -1 || s.indexOf('back order') > -1 ||
      s.indexOf('notif') > -1 || s.indexOf('stock') > -1) return 'backorder';
    return 'fit';
  }

  function copy() {
    return COPY[mode()] || COPY.fit;
  }

  /* ------------------------------------------------------ the selection -- */

  function indexOfUrl(url) {
    for (var i = 0; i < sel.length; i++) {
      if (sel[i].url === url) return i;
    }
    return -1;
  }

  /* The selection holds whatever URL it was given, which for a search hit
     carries Shopify's tracking query. A suggestion only knows the handle, so
     comparing the two needs the path alone. */
  function handleOf(url) {
    var m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  /* SHOPIFY'S SUGGEST ENDPOINT HANGS ITS OWN TRACKING ON EVERY URL (29 Oct).
     /search/suggest.json answers with
     /products/knott-jockey-wheel-jack?_pos=1&_psq=wheel&_psid=2f16f589&_ss=e,
     which is right for the storefront's own analytics and wrong for us twice
     over. It is what "Open the full part page" opens in the new tab, and it is
     what part_in_question_url carries onto the ticket, so the parts team ends
     up with a link holding somebody's half typed search in it. The handle is
     the only part that names the part, so everything after it goes. */
  function cleanUrl(url) {
    var s = String(url || '');
    if (!s) return '';
    return s.split('#')[0].split('?')[0] || s;
  }

  function hasHandle(h) {
    if (!h) return false;
    for (var i = 0; i < sel.length; i++) {
      if (handleOf(sel[i].url) === h) return true;
    }
    return false;
  }

  function add(part) {
    if (!part || !part.url || indexOfUrl(part.url) > -1) return;
    if (hasHandle(handleOf(part.url))) return;
    sel.push(part);
    announce();
  }

  function removeAt(i) {
    if (i < 0 || i >= sel.length) return;
    sel.splice(i, 1);
    if (sel.length < 2) view = 'list';
    announce();
  }

  /* ------------------------------------------------- what they have seen -- */

  /* assets/fx-seen.js ALREADY RECORDS THIS. Every part page the customer opens
     goes into localStorage under fxSeenParts, keyed by trailer, carrying the
     handle, title, price, stock state, image and a timestamp. That is exactly
     what a suggestion row needs, so the suggestions below cost no tracking
     code, no new endpoint and no extra request. If they ever go empty, check
     that fx-seen.js is still loading before suspecting anything here. */
  function seen() {
    try {
      var raw = window.localStorage.getItem('fxSeenParts');
      var rec = raw ? JSON.parse(raw) : null;
      var items = (rec && rec.items) || [];
      return items.slice().sort(function (a, b) {
        return (b.at || 0) - (a.at || 0);
      });
    } catch (e) {
      /* Private mode, blocked storage, or somebody else's shape in that key. */
      return [];
    }
  }

  function inStock(p) {
    return /^in stock/i.test(String(p && p.s || ''));
  }

  /* ON THE BACKORDER FORM ONLY SOLD OUT PARTS ARE OFFERED, because a backorder
     for something in stock is just an order, and offering one would send a
     customer who could buy today into a queue instead. The stock state stored
     by fx-seen.js is from the moment they looked rather than from right now,
     which is close enough for a suggestion: the part page itself is still the
     thing that decides whether they can buy. */
  function suggestions() {
    var wantOut = mode() === 'backorder';
    return seen().filter(function (p) {
      if (!p || !p.k) return false;
      if (hasHandle(p.k)) return false;
      return wantOut ? !inStock(p) : true;
    }).slice(0, SUGGEST);
  }

  /* ---------------------------------------------------------- rendering -- */

  /* A SPAN, NOT A BUTTON. The whole row is the button now: tapping anywhere on
     a part opens its details, where the picture can be zoomed. A button inside
     a button is invalid markup and the inner one swallows the outer one's
     clicks, which is what a separate zoom target here would have become. */
  function thumb(p) {
    var src = p.img ? sized(p.img, 180) : '';
    return '<span class="fxpp__thumb">' + (src
      ? '<img src="' + esc(src) + '" alt="" width="48" height="48" loading="lazy">'
      : '<span class="fxpp__noimg" aria-hidden="true"></span>') + '</span>';
  }

  function stockTag(available) {
    return '<span class="fxpp__stock' + (available ? '' : ' is-out') + '">' +
      (available ? 'In stock' : 'Out of stock') + '</span>';
  }

  function redraw() {
    var box = document.querySelector('[data-fxp-part]');
    if (!box) return;
    renderSelection(box);
    var res = box.querySelector('[data-fxp-results]');
    if (!res) return;
    /* Whichever of the two is showing gets rebuilt, so an add removes the row
       that was just used from either one. */
    if (res._list) renderResults(box, res._list, '');
    else if (res._sug) renderSuggest(box);
  }

  /* The details sheet in assets/fx-fitdetail.js adds and removes through this,
     so there is one list and one place allowed to change it. */
  window.fxFitApi = {
    has: function (url) { return indexOfUrl(url) > -1 || hasHandle(handleOf(url)); },
    add: function (part) { add(part); redraw(); },
    removeByUrl: function (url) { removeAt(indexOfUrl(url)); redraw(); }
  };

  function renderSelection(box) {
    var wrap = box.querySelector('[data-fxp-sel]');
    if (!wrap) return;

    if (!sel.length) {
      var msg = copy().empty;
      wrap.innerHTML = msg ? '<p class="fxpp__msg">' + esc(msg) + '</p>' : '';
      return;
    }

    var toggle = sel.length > 1
      ? '<div class="fxpp__views">' +
        '<button class="fxpp__view' + (view === 'list' ? ' is-on' : '') + '" type="button" ' +
        'data-fxp-view="list">List</button>' +
        '<button class="fxpp__view' + (view === 'gallery' ? ' is-on' : '') + '" type="button" ' +
        'data-fxp-view="gallery">Gallery</button>' +
        '</div>'
      : '';

    var body;
    if (view === 'gallery') {
      body = '<ul class="fxpp__gal">' + sel.map(function (p, i) {
        return '<li class="fxpp__card">' +
          '<button class="fxpp__open" type="button" data-fxp-open="s' + i + '">' +
          thumb(p) +
          '<span class="fxpp__card-t">' + esc(p.sku || p.title) + '</span>' +
          '</button>' +
          '<button class="fxpp__x" type="button" data-fxp-rm="' + i + '" ' +
          'aria-label="Remove ' + esc(label(p)) + '">' +
          '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" ' +
          'stroke-width="2.5" stroke-linecap="round" aria-hidden="true">' +
          '<path d="M6 6l12 12M18 6 6 18"/></svg></button>' +
          '</li>';
      }).join('') + '</ul>';
    } else {
      body = '<ul class="fxpp__list">' + sel.map(function (p, i) {
        return '<li class="fxpp__item">' +
          '<button class="fxpp__open" type="button" data-fxp-open="s' + i + '">' +
          thumb(p) +
          '<span class="fxpp__item-t">' + esc(label(p)) + '</span>' +
          '</button>' +
          '<button class="fxpp__x" type="button" data-fxp-rm="' + i + '" ' +
          'aria-label="Remove ' + esc(label(p)) + '">' +
          '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" ' +
          'stroke-width="2.5" stroke-linecap="round" aria-hidden="true">' +
          '<path d="M6 6l12 12M18 6 6 18"/></svg></button>' +
          '</li>';
      }).join('') + '</ul>';
    }

    wrap.innerHTML = toggle + body;
  }

  /* CARDS FOR SUGGESTIONS, A LIST FOR SEARCH RESULTS, and that difference is
     deliberate rather than an inconsistency to tidy up later. Picking from
     things you have already looked at is browsing, and you recognise a trailer
     part by its picture long before you recognise its name, so the picture
     leads. Typing "brake" and scanning for the right one is matching, and
     there the text you just typed is what the eye is looking for, so the text
     leads and more rows fit on the screen.

     A WRAPPING GRID, NOT THE HORIZONTAL SCROLLER used elsewhere on the store.
     The dialog is about 510px wide, so a sideways scroller would show two and
     a half cards and hide the rest behind a swipe, inside a container that is
     already scrolling vertically. Nested scroll areas are what people fumble
     on a phone. */
  function renderSuggest(box) {
    var results = box.querySelector('[data-fxp-results]');
    if (!results) return;

    var list = suggestions();
    results._list = null;
    results._sug = list;

    if (!list.length) {
      results.innerHTML = '';
      return;
    }

    results.innerHTML =
      '<div class="fxpp__sughead">' +
      '<span class="fxpp__sugh">Parts you’ve looked at</span>' +
      (mode() === 'backorder'
        ? '<span class="fxpp__sugnote">Out of stock only</span>'
        : '') +
      '</div>' +
      '<ul class="fxpp__sugs">' + list.map(function (p, i) {
        var src = p.img ? sized(p.img, 320) : '';
        return '<li class="fxpp__sug">' +
          '<button class="fxpp__sugopen" type="button" data-fxp-sugopen="' + i + '">' +
          (src
            ? '<img class="fxpp__sugimg" src="' + esc(src) + '" alt="" loading="lazy">'
            : '<span class="fxpp__sugimg fxpp__noimg" aria-hidden="true"></span>') +
          '<span class="fxpp__sugt">' + esc(p.t || p.k) + '</span>' +
          '</button>' +
          '<button class="fxpp__add fxpp__sugadd" type="button" ' +
          'data-fxp-sugadd="' + i + '">Add</button>' +
          '</li>';
      }).join('') + '</ul>' +
      '<p class="fxpp__msg fxpp__sugor">Or start typing a part name or number.</p>';
  }

  function renderResults(box, list, q) {
    var results = box.querySelector('[data-fxp-results]');
    if (!results) return;
    results._list = list;
    results._sug = null;

    if (!list) {
      results.innerHTML = '';
      return;
    }

    if (!list.length) {
      results.innerHTML = '<p class="fxpp__msg">Nothing matched "' + esc(q) + '". ' +
        'Try a shorter phrase, or describe it in the message instead.</p>';
      return;
    }

    /* available comes straight off the suggest payload, so the stock state
       costs nothing. The SKU does not: see withSku below. */
    results.innerHTML = '<ul class="fxpp__hits">' + list.map(function (p, i) {
      var on = indexOfUrl(p.url) > -1 || hasHandle(handleOf(p.url));
      var available = p.available !== false;
      return '<li class="fxpp__hit' + (on ? ' is-on' : '') + '">' +
        '<button class="fxpp__open" type="button" data-fxp-open="r' + i + '">' +
        thumb({ img: p.image || p.featured_image || '' }) +
        '<span class="fxpp__pick">' +
        '<span class="fxpp__pick-t">' + esc(p.title) + '</span>' +
        stockTag(available) +
        '</span>' +
        '</button>' +
        '<button class="fxpp__add" type="button" data-fxp-hit="' + i + '"' +
        (on ? ' disabled' : '') + '>' + (on ? 'Added' : 'Add') + '</button>' +
        '</li>';
    }).join('') + '</ul>';
  }

  /* ------------------------------------------------------- suggestions -- */

  function run(q, box) {
    var mine = ++seq;
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=' + LIMIT +
      '&resources[options][unavailable_products]=last';

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (mine !== seq) return;
        var list = (j && j.resources && j.resources.results && j.resources.results.products) || [];
        renderResults(box, list, q);
      })
      .catch(function () {
        if (mine !== seq) return;
        /* The suggest endpoint is down, not the form. Say so and get out of
           the way: they can still describe the part in the message field. */
        var results = box.querySelector('[data-fxp-results]');
        if (results) {
          results.innerHTML = '<p class="fxpp__msg">Part search is not responding. ' +
            'Describe the part in the message below and we will find it.</p>';
        }
      });
  }

  /* The suggest payload carries no SKU on this catalogue: every type is
     "Unclassified", every vendor is "0" from the Cin7 import, and the variants
     array comes back empty. One extra request on selection buys a part number
     the parts team can actually use. Suggestions go through here too, because
     fx-seen.js does not record the SKU either. */
  function withSku(p) {
    var handle = String(p.url || '').split('/products/')[1];
    var base = {
      title: p.title,
      sku: '',
      url: cleanUrl(p.url),
      img: p.image || p.featured_image || ''
    };
    if (!handle) return Promise.resolve(base);
    return fetch('/products/' + handle.split('?')[0] + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var v = j && j.variants && j.variants[0];
        base.sku = (v && v.sku) || '';
        if (!base.img && j && j.featured_image) base.img = j.featured_image;
        return base;
      })
      .catch(function () { return base; });
  }

  /* ------------------------------------------------------------ build -- */

  function pagePart() {
    var fit = document.querySelector('[data-fxp-fit]');
    if (!fit) return null;
    var title = fit.getAttribute('data-fxp-part-title');
    if (!title) return null;
    return {
      title: title,
      sku: fit.getAttribute('data-fxp-part-sku') || '',
      url: fit.getAttribute('data-fxp-part-url') || '',
      img: fit.getAttribute('data-fxp-part-img') || ''
    };
  }

  /* THE DIALOG IS REUSED AND ITS TITLE IS REWRITTEN ON EVERY OPEN. One
     .vcp-form serves both the backorder form and the fit check, and the
     extension sets the title when it opens, which is after this script has
     already built the picker. Anything captured once at build time is
     whichever flow opened the dialog first: an early build of this file put
     "Get notified" above a fit check form. Re-read all of it every pass, which
     is why the label and the placeholder are here and not only in build(). */
  function syncChrome() {
    var box = document.querySelector('[data-fxp-part]');
    if (!box) return;

    var titleEl = document.querySelector('.vcp-form__title');
    var head = box.querySelector('.fxpp__h');
    if (head && titleEl) {
      var t = titleEl.textContent.trim();
      if (t && head.textContent !== t) head.textContent = t;
    }

    var c = copy();
    var lab = box.querySelector('.fxpp__label');
    if (lab && lab.textContent !== c.label) lab.textContent = c.label;

    var inp = box.querySelector('[data-fxp-input]');
    if (inp && inp.getAttribute('placeholder') !== c.placeholder) {
      inp.setAttribute('placeholder', c.placeholder);
    }
  }

  /* HUBSPOT REPLACES THE FORM WITH ITS THANK YOU MESSAGE INSIDE THE IFRAME,
     and the picker lives outside it, above. So after a submit the chosen
     parts were still sitting there over a thank you note, as though the form
     were still waiting to be filled in. */
  function submitted() {
    try {
      var h = host();
      var mount = h && h.mount;
      var f = mount && mount.querySelector('iframe');
      var doc = f && f.contentDocument;
      if (!doc || !doc.body) return false;
      if (doc.querySelector('form')) return false;
      return (doc.body.textContent || '').trim().length > 0;
    } catch (e) {
      return false;
    }
  }

  /* Taken out rather than hidden, so reopening the modal rebuilds it. The
     selection is emptied with it: the next part someone asks about should
     start from that part, not from the last lot they already sent. */
  function clearOnSubmit() {
    var box = document.querySelector('[data-fxp-part]');
    if (!box && !sel.length) return;
    if (!submitted()) return;
    if (box) box.parentNode.removeChild(box);
    sel.length = 0;
    announce();
  }

  function build() {
    var h = host();
    if (!h) return;
    if (document.querySelector('[data-fxp-part]')) return;
    if (submitted()) return;

    var titleEl = document.querySelector('.vcp-form__title');
    var heading = (titleEl && titleEl.textContent.trim()) || 'Check it fits before you order';
    var c = copy();

    var box = document.createElement('div');
    box.className = 'fxpp';
    box.setAttribute('data-fxp-part', '');
    box.innerHTML =
      (h.heading ? '<h3 class="fxpp__h">' + esc(heading) + '</h3>' : '') +
      '<label class="fxpp__label">' + esc(c.label) + '</label>' +
      '<div data-fxp-sel></div>' +
      '<div class="fxpp__field">' +
      '<input class="fxpp__input" type="text" name="fxq" autocomplete="off" ' +
      'autocorrect="off" autocapitalize="off" spellcheck="false" ' +
      'placeholder="' + esc(c.placeholder) + '" ' +
      'aria-label="Search for a part to add" data-fxp-input>' +
      '</div>' +
      '<div class="fxpp__results" aria-live="polite" data-fxp-results></div>';
    /* Under the dialog's own sub heading in the modal, directly above the
       embed on the support page, where the section has drawn the heading. */
    if (h.after) h.after.insertAdjacentElement('afterend', box);
    else h.before.parentNode.insertBefore(box, h.before);

    var first = pagePart();
    if (first) add(first);
    renderSelection(box);

    box.addEventListener('click', function (e) {
      var t = e.target;

      /* Suggestions are checked before the generic openers, because their
         buttons carry their own attributes and would otherwise fall through
         to the search result branch and index into the wrong list. */
      var sugAdd = t.closest && t.closest('[data-fxp-sugadd]');
      if (sugAdd) {
        var ra = box.querySelector('[data-fxp-results]');
        var sa = ra && ra._sug && ra._sug[Number(sugAdd.getAttribute('data-fxp-sugadd'))];
        if (!sa) return;
        withSku({ title: sa.t || sa.k, url: '/products/' + sa.k, image: sa.img || '' })
          .then(function (part) {
            add(part);
            redraw();
          });
        return;
      }

      var sugOpen = t.closest && t.closest('[data-fxp-sugopen]');
      if (sugOpen) {
        var ro = box.querySelector('[data-fxp-results]');
        var so = ro && ro._sug && ro._sug[Number(sugOpen.getAttribute('data-fxp-sugopen'))];
        if (so && typeof window.fxFitOpenDetail === 'function') {
          window.fxFitOpenDetail({
            title: so.t || so.k,
            sku: '',
            url: '/products/' + so.k,
            img: so.img || ''
          });
        }
        return;
      }

      var opener = t.closest && t.closest('[data-fxp-open]');
      if (opener) {
        var key = opener.getAttribute('data-fxp-open');
        var n = Number(key.slice(1));
        var part = null;
        if (key.charAt(0) === 's') {
          part = sel[n];
        } else {
          var results = box.querySelector('[data-fxp-results]');
          var hit = results && results._list && results._list[n];
          if (hit) {
            part = {
              title: hit.title,
              sku: '',
              url: cleanUrl(hit.url),
              img: hit.image || hit.featured_image || ''
            };
          }
        }
        /* The sheet is a separate asset. If it failed to load, tapping a part
           must do nothing rather than throw on every row. */
        if (part && typeof window.fxFitOpenDetail === 'function') {
          window.fxFitOpenDetail(part);
        }
        return;
      }

      var rm = t.closest && t.closest('[data-fxp-rm]');
      if (rm) {
        removeAt(Number(rm.getAttribute('data-fxp-rm')));
        redraw();
        return;
      }

      var v = t.closest && t.closest('[data-fxp-view]');
      if (v) {
        view = v.getAttribute('data-fxp-view');
        renderSelection(box);
        return;
      }

      var pick = t.closest && t.closest('[data-fxp-hit]');
      if (!pick) return;
      var list = box.querySelector('[data-fxp-results]')._list;
      var chosen = list && list[Number(pick.getAttribute('data-fxp-hit'))];
      if (!chosen) return;
      withSku(chosen).then(function (part) {
        add(part);
        redraw();
      });
    });

    /* AN EMPTY FIELD NOW SHOWS SOMETHING. It used to clear the results and
       leave a blank space under the box, which is the one moment the customer
       most needs a nudge: they have opened a form about a part and have no
       idea they are allowed to add more. */
    box.addEventListener('focusin', function (e) {
      if (!e.target.matches || !e.target.matches('[data-fxp-input]')) return;
      if (e.target.value.trim().length >= 2) return;
      renderSuggest(box);
    });

    box.addEventListener('input', function (e) {
      if (!e.target.matches || !e.target.matches('[data-fxp-input]')) return;
      var q = e.target.value.trim();
      window.clearTimeout(timer);
      if (q.length < 2) {
        seq++;
        renderSuggest(box);
        return;
      }
      timer = window.setTimeout(function () { run(q, box); }, DEBOUNCE);
    });

    /* Enter inside a dialog that contains a form would submit it half filled. */
    box.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-fxp-input]')) {
        e.preventDefault();
      }
    });

    announce();
  }

  /* THE OBSERVER AT THE FOOT OF THIS FILE WATCHES THIS DOCUMENT, and HubSpot
     swaps the form for its thank you INSIDE the iframe, which never mutates
     this one. So nothing triggered a pass and the picker sat over the thank
     you until something unrelated happened. The frame's own document has to
     be watched too, once per frame. */
  function watchFrame() {
    try {
      var h = host();
      var mount = h && h.mount;
      var f = mount && mount.querySelector('iframe');
      var doc = f && f.contentDocument;
      if (!doc || !doc.body || doc.fxpWatched || !window.MutationObserver) return;
      doc.fxpWatched = true;
      new window.MutationObserver(schedule).observe(doc.body, {
        childList: true,
        subtree: true
      });
    } catch (e) {
      /* Cross origin. The picker still works, it just will not notice the
         thank you by itself. */
    }
  }

  function pass() {
    queued = null;
    try { build(); } catch (e) { /* dialog not built yet */ }
    try { watchFrame(); } catch (e) { /* no frame yet */ }
    try { clearOnSubmit(); } catch (e) { /* no frame to read */ }
    try { syncChrome(); } catch (e) { /* no picker on the page */ }
  }

  function schedule() {
    if (queued) return;
    queued = window.setTimeout(pass, 60);
  }

  function boot() {
    pass();
    if (!window.MutationObserver) return;
    new window.MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
